import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import './FAQs.css';

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Orders & Shipping',
      questions: [
        {
          question: 'How long does shipping take?',
          answer: 'Standard shipping typically takes 3-5 business days. Express shipping is available and takes 1-2 business days. International shipping may take 7-14 business days depending on the destination.'
        },
        {
          question: 'Do you offer free shipping?',
          answer: 'Yes! We offer free standard shipping on all orders over $50. For orders under $50, a flat shipping fee of $5.99 applies.'
        },
        {
          question: 'Can I track my order?',
          answer: 'Absolutely! Once your order ships, you\'ll receive a tracking number via email. You can also track your order by logging into your account and viewing your order history.'
        },
        {
          question: 'Do you ship internationally?',
          answer: 'Yes, we ship to most countries worldwide. International shipping rates and delivery times vary by location. Please check our shipping policy for more details.'
        }
      ]
    },
    {
      category: 'Returns & Exchanges',
      questions: [
        {
          question: 'What is your return policy?',
          answer: 'We accept returns within 30 days of purchase. Items must be unworn, unwashed, and in original condition with tags attached. Please visit our Returns page to initiate a return.'
        },
        {
          question: 'How do I exchange an item?',
          answer: 'To exchange an item, please return the original item and place a new order for the item you want. This ensures you get the size or color you need as quickly as possible.'
        },
        {
          question: 'Who pays for return shipping?',
          answer: 'For defective items or our errors, we cover return shipping. For other returns, customers are responsible for return shipping costs unless you have free return shipping as part of a promotion.'
        }
      ]
    },
    {
      category: 'Products & Sizing',
      questions: [
        {
          question: 'How do I find my size?',
          answer: 'Each product page includes a detailed size chart. We recommend measuring yourself and comparing to our size guide. If you\'re between sizes, we generally recommend sizing up for a more comfortable fit.'
        },
        {
          question: 'Are your products true to size?',
          answer: 'Yes, our products generally run true to size. However, sizing can vary slightly between styles. Check individual product pages for specific fit information and customer reviews.'
        },
        {
          question: 'How do I care for my garments?',
          answer: 'Care instructions are included on the label of each garment. Generally, we recommend washing in cold water and hanging to dry to maintain the quality and longevity of your items.'
        }
      ]
    },
    {
      category: 'Payment & Security',
      questions: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, Apple Pay, Google Pay, and various digital wallets. All payments are processed securely.'
        },
        {
          question: 'Is my payment information secure?',
          answer: 'Yes, absolutely. We use industry-standard SSL encryption to protect your payment information. We never store your complete credit card details on our servers.'
        },
        {
          question: 'Can I use multiple payment methods?',
          answer: 'Currently, we only support one payment method per order. However, you can use gift cards in combination with other payment methods.'
        }
      ]
    },
    {
      category: 'Account & Membership',
      questions: [
        {
          question: 'Do I need an account to place an order?',
          answer: 'No, you can checkout as a guest. However, creating an account allows you to track orders, save addresses, view order history, and receive exclusive member benefits.'
        },
        {
          question: 'How do I reset my password?',
          answer: 'Click on "Forgot Password" on the login page. Enter your email address, and we\'ll send you instructions to reset your password.'
        },
        {
          question: 'Can I change my email address?',
          answer: 'Yes, you can update your email address in your account settings. Log in, go to Profile Settings, and update your email address.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faqs-page">
      <div className="faqs-container">
        <div className="faqs-header">
          <Link to="/" className="back-link">
            <FiArrowLeft size={20} />
            Back to Home
          </Link>
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about our products, shipping, returns, and more.</p>
        </div>

        <div className="faqs-content">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="faq-category">
              <h2 className="category-title">{category.category}</h2>
              <div className="faq-list">
                {category.questions.map((faq, questionIndex) => {
                  const index = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openIndex === index;
                  
                  return (
                    <div key={questionIndex} className={`faq-item ${isOpen ? 'open' : ''}`}>
                      <button
                        className="faq-question"
                        onClick={() => toggleFAQ(categoryIndex, questionIndex)}
                      >
                        <span>{faq.question}</span>
                        {isOpen ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                      </button>
                      {isOpen && (
                        <div className="faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="faqs-contact">
          <h3>Still have questions?</h3>
          <p>Can't find the answer you're looking for? Please contact our customer support team.</p>
          <Link to="/contact" className="contact-btn">Contact Support</Link>
        </div>
      </div>
    </div>
  );
};

export default FAQs;
